import { Component, effect, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Table, TableFilterEvent, TableModule, TableRowSelectEvent, TableRowUnSelectEvent } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { Subject, takeUntil, map, Subscription } from 'rxjs';
import { TypePrestation } from './type-prestation.model';
import { ReferentialsPrestationsService } from './referentials-prestations.service';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { AddTypePrestationComponent } from './add-type-prestation/add-type-prestation.component';
import { AddPrestationComponent } from './add-prestation/add-prestation.component';
import { ContratsAssociesComponent } from './contrats-associes/contrats-associes.component';
import { Prestation } from './prestation.model';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { DropdownModule } from 'primeng/dropdown';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { MessageService, SelectItem } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { TooltipModule } from 'primeng/tooltip';
import { isEmpty } from '../../../../core/helpers/string-helper';
import { CheckboxModule } from 'primeng/checkbox';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";
import { ReferentialsUnitsService } from '../referentials-units/referentials-units.service';
import { Unites } from '../referentials-units/unites.model';

const dialogConfig = {
  modal: true,
  width: '90rem',
  maximizable: true,
  breakpoints: {
    '1199px': '75vw',
    '575px': '90vw',
  },
};

@Component({
  selector: 'app-referentials-prestations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    TableComponent,
    ButtonModule,
    AddTypePrestationComponent,
    AddPrestationComponent,
    ContratsAssociesComponent,
    DynamicDialogModule,
    ToastModule,
    TagModule,
    IconFieldModule,
    DropdownModule,
    ButtonModule,
    RippleModule,
    StyleClassModule,
    ReactiveFormsModule,
    TooltipModule,
    CheckboxModule,
    TranslocoModule
  ],
  providers: [DialogService, MessageService, ReferentialsPrestationsService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-prestations.component.html',
  styleUrl: './referentials-prestations.component.css',
})
export class ReferentialsPrestationsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('prestationTable') table!: Table;

  typePrestationResult!: TypePrestation[];
  prestationResult!: Prestation[];
  unites: Unites[] = [];
  lienDechets: SelectItem[] = [
    { value: null, label: 'TOUS' },
    { value: 'OPTIONNEL', label: 'OPTIONNEL' },
    { value: 'OBLIGATOIRE', label: 'OBLIGATOIRE' },
    { value: 'INTERDIT', label: 'INTERDIT' },
  ];

  typePrestationForm = this.formBuilder.group({
    typePrestationFormArray: this.formBuilder.array([]),
  });

  prestationForm = this.formBuilder.group({
    prestationFormArray: this.formBuilder.array([]),
  });

  selectedTypePrestation!: TypePrestation;
  typePrestation!: TypePrestation;

  selectedPrestation!: FormGroup;
  prestation!: Prestation;
  clonedPrestation: { [s: string]: Prestation } = {};
  clonedTypePrestation: { [s: string]: TypePrestation } = {};

  booleanValues = [true, false];

  errors: GenericErrorResponse[] = [];
  userHasReadWritePermission: boolean = false;

  ref: DynamicDialogRef | undefined;

  errorMessage?: string[];
  loading: boolean = false;
  isFirstRun: boolean = true;
  lineSelected: boolean = false;
  typePrestationTotalRecords = 0;
  prestationTotalRecords = 0;
  userHasReadOnly: boolean = false;
  rowIndex: number = 0;

  get typePrestationFormArray() {
    return this.typePrestationForm.get('typePrestationFormArray') as FormArray;
  }

  get prestationFormArray() {
    return this.prestationForm.get('prestationFormArray') as FormArray;
  }

  constructor(
    private storeService: StoreService,
    private formBuilder: FormBuilder,
    private prestationService: ReferentialsPrestationsService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private filterEventService: FilterEventService,
    private referentialsUnitsService: ReferentialsUnitsService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.userHasReadOnly = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RO');
    this.loadUnites();
    this.loadTypePrestation();
    this.loadPrestation();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadUnites() {
    this.referentialsUnitsService
      .getUnitesData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((unites) => {
        this.unites = unites;
      });
  }

  private loadTypePrestation() {
    this.loading = true;
    this.prestationService
      .getTypePrestation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (typePresta) => {
          this.typePrestationResult = typePresta;
          if (!!this.typePrestationFormArray) {
            this.typePrestationFormArray.clear();
          }

          this.typePrestationResult.forEach((typePrestationLine) => {
            if (this.typePrestationFormArray) {
              this.typePrestationFormArray.push(
                this.formBuilder.group({
                  id: [typePrestationLine.id],
                  codeHecate: [typePrestationLine.codeHecate],
                  designation: [!isEmpty(typePrestationLine.designation) ? typePrestationLine.designation : ''],
                  envoiTennaxia: [typePrestationLine.envoiTennaxia],
                  suffisantPourDemande: [typePrestationLine.suffisantPourDemande],
                  transport: [typePrestationLine.transport],
                  afficherSurLaConfirmation: [typePrestationLine.afficherSurLaConfirmation],
                  unite: [typePrestationLine.unite],
                }),
              );
              this.typePrestationTotalRecords = this.typePrestationResult.length;
            }
          });
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  editTypePrestation(typePrestatForm: FormGroup) {
    const rowValue = typePrestatForm.value;
    this.clonedTypePrestation[rowValue.id] = { ...rowValue };
  }

  saveRowTypePrestation(typePrestatForm: FormGroup) {
    const typePrestation: TypePrestation = typePrestatForm.value;
    this.prestationService
      .updateTypePrestation(typePrestation)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (inlineResponse) => {
          if (inlineResponse.success) {
            this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: inlineResponse.message });
            typePrestatForm.setValue(inlineResponse.object as TypePrestation);
          } else {
            this.errors = inlineResponse.errors.length > 0 ? inlineResponse.errors : [inlineResponse.exception];
            this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
        },
      });
  }

  cancelEditRowTypePrestation(typePrestatForm: FormGroup) {
    const id = typePrestatForm.value.id;
    if (this.clonedTypePrestation[id]) {
      typePrestatForm.setValue(this.clonedTypePrestation[id]);
      delete this.clonedTypePrestation[id];
    }
  }

  addNewTypePrestation() {
    this.ref = this.dialogService.open(AddTypePrestationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
      data: {
        typePrestationForm: this.typePrestationForm,
        userHasReadWritePermission: this.userHasReadWritePermission,
      },
    });

    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.loadTypePrestation();
      }
    });
  }

  private loadPrestation() {
    this.loading = true;
    this.prestationService
      .getPrestation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (presta) => {
          this.prestationResult = presta;
          if (!!this.prestationFormArray) {
            this.prestationFormArray.clear();
          }
          this.prestationResult.forEach((pestationLine) => {
            if (this.prestationFormArray) {
              this.prestationFormArray.push(
                this.formBuilder.group({
                  id: [pestationLine.id],
                  codeHecate: [pestationLine.codeHecate],
                  designation: [!isEmpty(pestationLine.designation) ? pestationLine.designation : ''],
                  codeDR: [!isEmpty(pestationLine.codeDR) ? pestationLine.codeDR : ''],
                  libelleCaracteristiques: [!isEmpty(pestationLine.libelleCaracteristiques) ? pestationLine.libelleCaracteristiques : ''],
                  typeDesignation: [!isEmpty(pestationLine.typeDesignation) ? pestationLine.typeDesignation : ''],
                  typeCodeHecate: [pestationLine.typeCodeHecate],
                  typeId: [pestationLine.typeId],
                  variante: [pestationLine.variante],
                  lienDechet: [pestationLine.lienDechet],
                  categorieAchatMosaic: [pestationLine.categorieAchatMosaic],
                  codeTennaxia: [!isEmpty(pestationLine.codeTennaxia) ? pestationLine.codeTennaxia : ''],
                }),
              );
              this.prestationTotalRecords = this.prestationResult.length;
            }
          });
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  editPrestation(prestationForm: FormGroup) {
    const rowValue = prestationForm.value;
    this.clonedPrestation[rowValue.id] = { ...rowValue };
  }

  saveRowPrestation(prestaForm: FormGroup) {
    const prestation: Prestation = prestaForm.value;
    this.prestationService
      .updatePrestation(prestation)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (inlineResponse) => {
          if (inlineResponse.success) {
            this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: inlineResponse.message });
            prestaForm.setValue(inlineResponse.object as Prestation);
          } else {
            this.errors = inlineResponse.errors.length > 0 ? inlineResponse.errors : [inlineResponse.exception];
            this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
        },
      });
  }

  cancelEditRowPrestation(prestationForm: FormGroup) {
    const id = prestationForm.value.id;
    if (this.clonedTypePrestation[id]) {
      prestationForm.setValue(this.clonedTypePrestation[id]);
      delete this.clonedTypePrestation[id];
    }
  }

  addNewPrestation() {
    this.ref = this.dialogService.open(AddPrestationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
      data: {
        prestationForm: this.prestationForm,
        userHasReadWritePermission: this.userHasReadWritePermission,
      },
    });

    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.loadPrestation();
      }
    });
  }

  viewOrEditContratsAssocieePrestation() {
    this.ref = this.dialogService.open(ContratsAssociesComponent, {
      ...dialogConfig,
      header: 'Associer contrats',
      data: {
        prestationId: this.selectedPrestation.value.id,
        userHasReadWritePermission: this.userHasReadWritePermission,
      },
    });

    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.loadPrestation();
      }
    });
  }

  onRowSelect($event: TableRowSelectEvent) {
    if (!!this.selectedPrestation) {
      this.lineSelected = true;
    }
  }

  onRowDeselect($event: TableRowUnSelectEvent) {
    if (!this.selectedPrestation) {
      this.lineSelected = false;
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
